import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Upload, History, SlidersHorizontal, Linkedin, Plug, Download } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ImportWizard from "../components/import/ImportWizard";
import ImportHistory from "../components/import/ImportHistory";
import ImportPresets from "../components/import/ImportPresets";
import ImportBatchDetail from "../components/import/ImportBatchDetail";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function TemplateLinks({ entities }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="template-links">
      {entities.map((e) => (
        <div key={e.key} className="flex items-center gap-1 text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5">
          <Download className="w-3.5 h-3.5 text-teal-600" /><span className="text-slate-700">{e.label}</span>
          <a data-testid={`template-${e.key}-csv`} className="text-teal-700 hover:underline font-mono ml-1" href={`${API}/import/templates/${e.key}?format=csv`}>CSV</a>
          <a data-testid={`template-${e.key}-xlsx`} className="text-teal-700 hover:underline font-mono" href={`${API}/import/templates/${e.key}?format=xlsx`}>XLSX</a>
        </div>
      ))}
    </div>
  );
}

function LinkedInSection({ meta }) {
  return (
    <div className="space-y-4" data-testid="linkedin-section">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <p className="font-heading font-semibold text-slate-800 mb-2">Flusso conforme LinkedIn / Sales Navigator / Linked Helper → CRM</p>
        <ol className="text-sm text-slate-600 list-decimal pl-5 space-y-1">
          <li>Esporta la lista Lead/Account da Sales Navigator (CSV), le connessioni da LinkedIn (CSV) o la campagna da Linked Helper (CSV/XLSX).</li>
          <li>Caricala in <b>Nuova importazione</b>: il CRM riconosce automaticamente il preset (LinkedIn, Sales Navigator o Linked Helper) e la Source corretta.</li>
          <li>Entità consigliata <b>Aziende + Contatti</b>: l'Account viene riconosciuto o creato una sola volta, ogni persona viene collegata; opzionalmente crea il Lead.</li>
          <li>Deduplica: Account per dominio / LinkedIn Company URL / ragione sociale; persona per email / LinkedIn URL / Sales Navigator URL / nome+cognome+azienda.</li>
          <li>Ogni record conserva Source, data import, batch ID, file e utente.</li>
        </ol>
      </div>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
        <b>Integrazione API ufficiale: non attiva.</b> Il programma LinkedIn SNAP è chiuso a nuovi partner e CRM Sync richiede licenza Advanced Plus su CRM supportati. Nessuno scraping, nessuna API non ufficiale, nessuna connessione diretta: solo import file + architettura predisposta (vedi Integrazioni).
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="font-semibold text-slate-700 mb-1.5">Account / Azienda riconosciuti</p><p className="text-slate-500">Company Name, Company LinkedIn URL, Website, Domain, Industry, Company Size, Country, Region, City</p></div>
        <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="font-semibold text-slate-700 mb-1.5">Lead / Persona riconosciuti</p><p className="text-slate-500">First/Last Name, Job Title, Seniority, Function, Department, LinkedIn Profile URL, Sales Navigator URL, Company, Country, Region, Email, Phone, Mobile</p></div>
      </div>
    </div>
  );
}

function Integrations() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/import/integrations").then(({ data }) => setItems(data)); }, []);
  const color = { predisposto: "bg-teal-50 text-teal-700", pianificato: "bg-slate-100 text-slate-600", non_disponibile: "bg-red-50 text-red-700", via_csv: "bg-amber-50 text-amber-700" };
  return (
    <div className="space-y-2" data-testid="integrations-list">
      {items.map((i) => (
        <div key={i.key} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3">
          <Plug className="w-4 h-4 text-slate-400 mt-0.5" />
          <div className="flex-1"><p className="text-sm font-medium text-slate-800">{i.name}</p><p className="text-xs text-slate-500 mt-0.5">{i.note}</p></div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono ${color[i.status]}`}>{i.status.replace("_", " ")}</span>
        </div>
      ))}
    </div>
  );
}

export default function ImportCenter() {
  const { batchId } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("nuova");

  useEffect(() => { if (can("import")) api.get("/import/meta").then(({ data }) => setMeta(data)); }, [can]);

  if (!can("import")) return <div className="text-slate-500 py-10 text-center" data-testid="import-no-access">Non hai i permessi per accedere all'Import Center.</div>;
  if (!meta) return <div className="text-slate-400 py-10">Caricamento…</div>;

  if (batchId) return <ImportBatchDetail batchId={batchId} meta={meta} onBack={() => nav("/import")} />;

  return (
    <div>
      <PageHeader title="Import Center & Data Sync" subtitle="Importazione controllata di database esterni: upload → anteprima → mapping → deduplica → validazione → conferma" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-slate-200 h-auto flex-wrap">
          <TabsTrigger value="nuova" data-testid="import-tab-nuova" className="gap-1.5"><Upload className="w-3.5 h-3.5" /> Nuova importazione</TabsTrigger>
          <TabsTrigger value="storico" data-testid="import-tab-storico" className="gap-1.5"><History className="w-3.5 h-3.5" /> Storico</TabsTrigger>
          <TabsTrigger value="preset" data-testid="import-tab-preset" className="gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5" /> Preset</TabsTrigger>
          <TabsTrigger value="linkedin" data-testid="import-tab-linkedin" className="gap-1.5"><Linkedin className="w-3.5 h-3.5" /> LinkedIn / Sales Navigator</TabsTrigger>
          <TabsTrigger value="integrazioni" data-testid="import-tab-integrazioni" className="gap-1.5"><Plug className="w-3.5 h-3.5" /> Integrazioni</TabsTrigger>
        </TabsList>
        <TabsContent value="nuova" className="mt-4"><ImportWizard meta={meta} onDone={(id) => nav(`/import/${id}`)} /></TabsContent>
        <TabsContent value="storico" className="mt-4"><ImportHistory onOpen={(id) => nav(`/import/${id}`)} /></TabsContent>
        <TabsContent value="preset" className="mt-4"><ImportPresets meta={meta} /></TabsContent>
        <TabsContent value="linkedin" className="mt-4"><LinkedInSection meta={meta} /></TabsContent>
        <TabsContent value="integrazioni" className="mt-4"><Integrations /></TabsContent>
      </Tabs>
    </div>
  );
}
